import React from "react";
import {
  Box,
  Button,
  Typography,
  useMediaQuery,
} from "@mui/material";
import {
  PublicOutlined,
  LockOutlined,
  HandshakeOutlined,
  CloseOutlined,
  OpenInNewOutlined,
} from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import { dt, accentGradientDark } from "../../../shared/themes/redesignTokens";

/* ===================================================================
   PARTNER TYPES (exported for use in EditServiceRequestPage +
   SelectPartnersDialog)
   =================================================================== */

export interface PartnerCompany {
  customerId: string;
  name: string;
  tierCode: number;
  logoUrl?: string;
}

export const tierLabels: Record<number, string> = {
  1: "Connected",
  2: "Preferred",
  3: "Contracted",
};

export const tierColors: Record<number, { bg: string; text: string; border: string }> =
  {
    1: { bg: "#EBF5FB", text: dt.blue, border: dt.blue },
    2: { bg: "#FEF9E7", text: "#F39C12", border: "#F39C12" },
    3: { bg: "#E8F8F5", text: "#1ABC9C", border: "#1ABC9C" },
  };

/* ===================================================================
   PROPS
   =================================================================== */

export interface VisibilitySelectorProps {
  visibilityCode: number;
  setVisibilityCode: (code: number) => void;
  targetPartnerIds: string[];
  setTargetPartnerIds: (ids: string[]) => void;
  partners: PartnerCompany[];
  partnersLoading: boolean;
  visibilityError?: string;
  setVisibilityError: (err: string | undefined) => void;
  translate: (key: string, options?: any) => any;
  onOpenSelectPartners: () => void;
}

/* ===================================================================
   COMPONENT
   =================================================================== */

const VisibilitySelector = ({
  visibilityCode,
  setVisibilityCode,
  targetPartnerIds,
  setTargetPartnerIds,
  partners,
  partnersLoading,
  visibilityError,
  setVisibilityError,
  translate,
  onOpenSelectPartners,
}: VisibilitySelectorProps) => {
  const isPhone = useMediaQuery("(max-width:768px)");
  const navigate = useNavigate();
  const isPublic = visibilityCode === 1;
  const isPartner = visibilityCode === 2;

  // Get partner info by customerId for chip display
  const getPartnerById = (customerId: string) =>
    partners.find((p) => p.customerId === customerId);

  const handleRemovePartner = (customerId: string) => {
    setTargetPartnerIds(targetPartnerIds.filter((id) => id !== customerId));
    setVisibilityError(undefined);
  };

  return (
    <Box
      sx={{
        animation: "fadeUp 0.4s cubic-bezier(0.4,0,0.2,1) both",
      }}
    >
      {/* Section heading */}
      <Typography
        sx={{
          fontFamily: dt.font,
          fontSize: "11px",
          fontWeight: 700,
          textTransform: "uppercase",
          letterSpacing: "1.2px",
          color: dt.textSecondary,
          mb: 2,
        }}
      >
        {translate("servicing:visibility.label")}
      </Typography>

      {/* -- Card options ----------------------------------- */}
      <Box
        sx={{
          display: "flex",
          flexDirection: isPhone ? "column" : "row",
          gap: 2,
        }}
      >
        {/* PUBLIC card */}
        <Box
          onClick={() => {
            setVisibilityCode(1);
            setTargetPartnerIds([]);
            setVisibilityError(undefined);
          }}
          sx={{
            flex: 1,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: 1.5,
            p: 3,
            borderRadius: dt.radius,
            border: `2px solid ${isPublic ? dt.blue : dt.cardBorder}`,
            backgroundColor: isPublic ? "rgba(52, 152, 219, 0.04)" : dt.cardBg,
            boxShadow: isPublic
              ? `0 0 0 3px ${dt.blueGlow}, ${dt.cardShadow}`
              : dt.cardShadow,
            cursor: "pointer",
            transition: "all 0.25s cubic-bezier(0.4,0,0.2,1)",
            "&:hover": {
              borderColor: isPublic ? dt.blue : "#D1D5DB",
              boxShadow: dt.cardShadowHover,
              transform: "translateY(-2px)",
            },
          }}
        >
          <Box
            sx={{
              width: 48,
              height: 48,
              borderRadius: "50%",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              background: isPublic
                ? accentGradientDark
                : dt.inputBg,
              transition: "all 0.25s ease",
            }}
          >
            <PublicOutlined
              sx={{
                fontSize: "24px",
                color: isPublic ? "#fff" : dt.textMuted,
              }}
            />
          </Box>

          <Typography
            sx={{
              fontFamily: dt.font,
              fontSize: "15px",
              fontWeight: 700,
              color: isPublic ? dt.blue : dt.textPrimary,
            }}
          >
            {translate("servicing:visibility.public")}
          </Typography>

          <Typography
            sx={{
              fontFamily: dt.font,
              fontSize: "12px",
              color: dt.textSecondary,
              textAlign: "center",
              lineHeight: 1.5,
            }}
          >
            {translate("servicing:visibility.publicDescription")}
          </Typography>
        </Box>

        {/* SEND TO PARTNER card */}
        <Box
          onClick={() => {
            setVisibilityCode(2);
            setVisibilityError(undefined);
          }}
          sx={{
            flex: 1,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: 1.5,
            p: 3,
            borderRadius: dt.radius,
            border: `2px solid ${isPartner ? "#27AE60" : dt.cardBorder}`,
            backgroundColor: isPartner
              ? "rgba(39, 174, 96, 0.04)"
              : dt.cardBg,
            boxShadow: isPartner
              ? `0 0 0 3px rgba(39,174,96,0.12), ${dt.cardShadow}`
              : dt.cardShadow,
            cursor: "pointer",
            transition: "all 0.25s cubic-bezier(0.4,0,0.2,1)",
            "&:hover": {
              borderColor: isPartner ? "#27AE60" : "#D1D5DB",
              boxShadow: dt.cardShadowHover,
              transform: "translateY(-2px)",
            },
          }}
        >
          <Box
            sx={{
              width: 48,
              height: 48,
              borderRadius: "50%",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              background: isPartner
                ? "linear-gradient(135deg, #27AE60 0%, #219653 100%)"
                : dt.inputBg,
              transition: "all 0.25s ease",
            }}
          >
            <LockOutlined
              sx={{
                fontSize: "24px",
                color: isPartner ? "#fff" : dt.textMuted,
              }}
            />
          </Box>

          <Typography
            sx={{
              fontFamily: dt.font,
              fontSize: "15px",
              fontWeight: 700,
              color: isPartner ? "#27AE60" : dt.textPrimary,
            }}
          >
            {translate("servicing:visibility.sendToPartner")}
          </Typography>

          <Typography
            sx={{
              fontFamily: dt.font,
              fontSize: "12px",
              color: dt.textSecondary,
              textAlign: "center",
              lineHeight: 1.5,
            }}
          >
            {translate("servicing:visibility.sendToPartnerDescription")}
          </Typography>
        </Box>
      </Box>

      {/* -- Partner selection area (when Send to Partner selected) -- */}
      {isPartner && (
        <Box
          sx={{
            mt: 2.5,
            animation: "fadeUp 0.3s cubic-bezier(0.4,0,0.2,1) both",
          }}
        >
          {partnersLoading ? (
            <Typography
              sx={{
                fontFamily: dt.font,
                fontSize: "13px",
                color: dt.textMuted,
              }}
            >
              ...
            </Typography>
          ) : partners.length === 0 ? (
            /* No partners — show message + Business Network link */
            <Box
              sx={{
                p: 2.5,
                borderRadius: dt.radiusSm,
                border: `1px solid ${dt.cardBorder}`,
                backgroundColor: dt.inputBg,
              }}
            >
              <Typography
                sx={{
                  fontFamily: dt.font,
                  fontSize: "13px",
                  color: dt.textSecondary,
                  lineHeight: 1.6,
                  mb: 1.5,
                }}
              >
                {translate("servicing:visibility.noPartnersMessage")}
              </Typography>
              <Button
                size="small"
                variant="outlined"
                startIcon={<OpenInNewOutlined sx={{ fontSize: "16px !important" }} />}
                onClick={() => navigate("/companies")}
                sx={{
                  textTransform: "none",
                  fontFamily: dt.font,
                  fontWeight: 600,
                  fontSize: "12px",
                  borderRadius: dt.radiusSm,
                  borderColor: "#27AE60",
                  color: "#27AE60",
                  "&:hover": {
                    borderColor: "#219653",
                    backgroundColor: "rgba(39,174,96,0.04)",
                  },
                }}
              >
                {translate("servicing:visibility.goToBusinessNetwork")}
              </Button>
            </Box>
          ) : (
            /* Partners exist — show select button + chips */
            <Box>
              {/* Select / Change Partners button */}
              <Button
                variant="outlined"
                startIcon={<HandshakeOutlined sx={{ fontSize: "18px !important" }} />}
                onClick={onOpenSelectPartners}
                sx={{
                  textTransform: "none",
                  fontFamily: dt.font,
                  fontWeight: 600,
                  fontSize: "13px",
                  borderRadius: dt.radiusSm,
                  borderColor: "#27AE60",
                  color: "#27AE60",
                  px: 2.5,
                  py: 1,
                  "&:hover": {
                    borderColor: "#219653",
                    backgroundColor: "rgba(39,174,96,0.04)",
                  },
                }}
              >
                {targetPartnerIds.length > 0
                  ? translate("servicing:visibility.changePartnersButton")
                  : translate("servicing:visibility.selectPartnersButton")}
              </Button>

              {/* Selected partner chips */}
              {targetPartnerIds.length > 0 && (
                <Box sx={{ mt: 1.5 }}>
                  <Box
                    sx={{
                      display: "flex",
                      flexWrap: "wrap",
                      gap: 1,
                    }}
                  >
                    {targetPartnerIds.map((id) => {
                      const partner = getPartnerById(id);
                      if (!partner) return null;
                      const tc = tierColors[partner.tierCode];
                      return (
                        <Box
                          key={id}
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 0.75,
                            px: 1.5,
                            py: 0.5,
                            borderRadius: dt.radiusPill,
                            backgroundColor: dt.cardBg,
                            border: `1px solid ${dt.cardBorder}`,
                            borderLeft: `3px solid ${tc?.border || dt.cardBorder}`,
                            transition: "all 0.15s ease",
                            "&:hover": {
                              boxShadow: dt.cardShadowHover,
                            },
                          }}
                        >
                          {/* Mini avatar */}
                          {partner.logoUrl ? (
                            <Box
                              component="img"
                              src={partner.logoUrl}
                              alt=""
                              sx={{
                                width: 20,
                                height: 20,
                                borderRadius: "4px",
                                objectFit: "cover",
                                flexShrink: 0,
                              }}
                            />
                          ) : (
                            <Box
                              sx={{
                                width: 20,
                                height: 20,
                                borderRadius: "4px",
                                backgroundColor: "#EBF5FB",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                flexShrink: 0,
                                fontFamily: dt.font,
                                fontSize: "10px",
                                fontWeight: 700,
                                color: dt.blue,
                              }}
                            >
                              {partner.name?.charAt(0)?.toUpperCase() || "?"}
                            </Box>
                          )}

                          <Typography
                            sx={{
                              fontFamily: dt.font,
                              fontSize: "12px",
                              fontWeight: 500,
                              color: dt.textPrimary,
                              maxWidth: "150px",
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                            }}
                          >
                            {partner.name}
                          </Typography>

                          {/* Remove button */}
                          <Box
                            onClick={(e) => {
                              e.stopPropagation();
                              handleRemovePartner(id);
                            }}
                            sx={{
                              cursor: "pointer",
                              display: "flex",
                              alignItems: "center",
                              color: dt.textMuted,
                              "&:hover": { color: "#E74C3C" },
                              transition: "color 0.15s",
                            }}
                          >
                            <CloseOutlined sx={{ fontSize: "14px" }} />
                          </Box>
                        </Box>
                      );
                    })}
                  </Box>

                  {/* Selected count */}
                  <Typography
                    sx={{
                      fontFamily: dt.font,
                      fontSize: "12px",
                      color: "#27AE60",
                      mt: 1,
                      fontWeight: 500,
                    }}
                  >
                    {translate("servicing:visibility.partnersSelected", {
                      count: targetPartnerIds.length,
                    })}
                  </Typography>
                </Box>
              )}

              {/* Error message */}
              {visibilityError && (
                <Typography
                  sx={{
                    fontFamily: dt.font,
                    fontSize: "12px",
                    color: "#E74C3C",
                    mt: 1,
                  }}
                >
                  {visibilityError}
                </Typography>
              )}
            </Box>
          )}
        </Box>
      )}
    </Box>
  );
};

export default VisibilitySelector;
